import type { FitCastBoardContext } from "../FitCast/context";
import { resolveCastWorldBounds } from "./resolveBounds";
import { activeBeat, type BookTimelineLayout } from "@/lib/timeline/book/BookLayout";
import { highestLabelNear } from "@/lib/timeline/chapter/ChapterTick/labelTops";
import {
  includeActiveLocations,
  type CastBounds,
} from "../FitCast/locations";
import { MARKER_DIAMETER, timelineRailAxisY } from "../../marker/MarkerMetrics/metrics";
import { markerRailPose } from "../../rail/RailPose/pose";
import { parkedVehicleNeedsRoom } from "../../rail/RailVehicleRow/row";
import { computeSceneMotion } from "@/lib/timeline/scene/SceneMotion/motion";
import { timelineCameraZoom } from "../../view/TimelineCameraZoom/zoom";
import { computeTimelineFollowView } from "../../view/TimelineFollowView/follow";
import type { TimelineViewTransform } from "../../view/ViewTransform/transform";
import { viewTransformCenteredOn } from "../../view/ViewTransform/transform";
import { visibleCharacterCenter, zoomToFitAroundCenter } from "./characterCenter";
import { clampTimelineZoom, TIMELINE_MIN_ZOOM } from "../../view/WorldMetrics/metrics";

const NAME_BELOW_DISC_PX = 88;
const SCREEN_PAD_PX = 40;

/** Fit zoom from the cast and labels. Pan stays on the visible characters. */
export function frameLockedCamera(
  layout: BookTimelineLayout,
  stageW: number,
  stageH: number,
  viewportWidth: number,
  playheadMs: number,
  heldZoom: number | null,
  boardContext?: FitCastBoardContext | null,
): TimelineViewTransform {
  const center = visibleCharacterCenter(
    layout, viewportWidth, playheadMs, boardContext,
  );
  if (heldZoom !== null) {
    if (center === null) {
      return computeTimelineFollowView(
        layout, stageW, stageH, viewportWidth, playheadMs, heldZoom,
      );
    }
    return viewTransformCenteredOn(center.x, center.y, heldZoom, stageW, stageH);
  }
  const bounds = castWorldBounds(layout, viewportWidth, playheadMs);
  if (center === null || bounds === null) {
    return fitCastInView(
      layout, stageW, stageH, viewportWidth, playheadMs, boardContext,
    );
  }
  // Pan X stays on the playhead. Y centers everything in frame, so no height goes unused.
  const middle = { x: center.x, y: (bounds.minY + bounds.maxY) / 2 };
  const zoom = clampTimelineZoom(zoomToFitAroundCenter(
    bounds, middle, stageW, stageH, SCREEN_PAD_PX,
  ));
  return viewTransformCenteredOn(middle.x, middle.y, zoom, stageW, stageH);
}

/** Zoom and center so the current cast and their location labels fit. */
export function fitCastInView(
  layout: BookTimelineLayout,
  stageW: number,
  stageH: number,
  viewportWidth: number,
  playheadMs: number,
  boardContext?: FitCastBoardContext | null,
): TimelineViewTransform {
  const bounds = resolveCastWorldBounds(
    layout,
    viewportWidth,
    playheadMs,
    boardContext,
  );
  if (bounds === null) {
    const count = activeBeat(layout, playheadMs)?.characterIds.length ?? 2;
    return computeTimelineFollowView(
      layout,
      stageW,
      stageH,
      viewportWidth,
      playheadMs,
      timelineCameraZoom(stageW, count),
    );
  }
  const pad = Math.min(SCREEN_PAD_PX, stageW * 0.06, stageH * 0.06);
  const width = Math.max(1, bounds.maxX - bounds.minX);
  const height = Math.max(1, bounds.maxY - bounds.minY);
  const zoom = clampTimelineZoom(Math.min(
    (stageW - pad * 2) / width,
    (stageH - pad * 2) / height,
  ));
  if (zoom <= TIMELINE_MIN_ZOOM * 1.05) {
    const count = activeBeat(layout, playheadMs)?.characterIds.length ?? 2;
    return computeTimelineFollowView(
      layout,
      stageW,
      stageH,
      viewportWidth,
      playheadMs,
      timelineCameraZoom(stageW, count),
    );
  }
  return viewTransformCenteredOn(
    (bounds.minX + bounds.maxX) / 2,
    (bounds.minY + bounds.maxY) / 2,
    zoom,
    stageW,
    stageH,
  );
}

export function castWorldBounds(
  layout: BookTimelineLayout,
  viewportWidth: number,
  playheadMs: number,
): CastBounds | null {
  const motion = computeSceneMotion(
    playheadMs,
    viewportWidth,
    layout.totalDurationMs,
  );
  const railY = timelineRailAxisY();
  let bounds: CastBounds | null = null;
  for (const marker of layout.markers) {
    const pose = markerRailPose(marker.characterId, playheadMs, layout.rail);
    if (pose === null || (pose.parked === true && !parkedVehicleNeedsRoom(pose))) {
      continue;
    }
    // A vehicle still coming adds its row's height, not how far off to the side it is.
    const cx = motion.groupCenterX + (pose.parked === true ? 0 : pose.dx);
    const cy = railY + pose.railDy + pose.dy;
    const half = MARKER_DIAMETER / 2;
    bounds = include(
      bounds,
      cx - half,
      cy - half,
      cx + half,
      cy + half + NAME_BELOW_DISC_PX,
    );
  }
  const framed = includeActiveLocations(
    bounds,
    layout,
    viewportWidth,
    playheadMs,
  );
  const labelTop = highestLabelNear(layout, motion.groupCenterX, viewportWidth);
  return framed === null || labelTop === null
    ? framed
    : { ...framed, minY: Math.min(framed.minY, labelTop) };
}

function include(
  bounds: CastBounds | null,
  minX: number,
  minY: number,
  maxX: number,
  maxY: number,
): CastBounds {
  if (bounds === null) {
    return { minX, minY, maxX, maxY };
  }
  return {
    minX: Math.min(bounds.minX, minX),
    minY: Math.min(bounds.minY, minY),
    maxX: Math.max(bounds.maxX, maxX),
    maxY: Math.max(bounds.maxY, maxY),
  };
}
